import $ from 'cash-dom'
import { Auth } from 'aws-amplify'
import { getCookie, setCookie } from '../utilities/cookies'
import { createFormStateManager } from '../utilities/form-manager'
import { checkMigration } from './check-migration'
import segment from '../tracking/segment'
import { callMultipass } from './multipass-retry'
import { jwtDecode, JwtPayload } from 'jwt-decode'

interface CognitoJwtPayload extends JwtPayload {
  email?: string
  given_name?: string
  family_name?: string
  name?: string
  'custom:firebase_uid'?: string
  'custom:puid'?: string
  'custom:pura_user_incomplete': string
}

const signInFormManager = createFormStateManager({
  formSelector: '#email-sign-in-form',
  defaultText: 'Sign In',
})

const emailVerifyFormManager = createFormStateManager({
  formSelector: '#account-email-verify-form',
  defaultText: 'Verify Email',
})

const completeSignupFormManager = createFormStateManager({
  formSelector: '#account-complete-signup-form',
  defaultText: 'Create Account',
})

const completeNewAccountFormManager = createFormStateManager({
  formSelector: '#account-complete-new-account-form',
  defaultText: 'Create new account',
})

const passwordFormManager = createFormStateManager({
  formSelector: '#account-password-form',
  defaultText: 'Continue',
})

const subsystemURL = window.Pura?.environment?.production
  ? 'https://user.trypura.io'
  : 'https://user.stg.trypura.io'

const hubSignInFailure = (message: string) => {
  signInFormManager.setFailure(message)
}

const hubSignIn = async (data: any) => {
  try {
    const return_to_url = getCookie('login_redirect') || window.location.origin
    const idToken = data?.signInUserSession?.idToken || {}

    let cognitoToken = idToken?.jwtToken
    const cognitoEmail = idToken?.payload?.email
    const cognitoFirebaseID = idToken?.payload?.['custom:firebase_uid']
    const cognitoFirstName = idToken?.payload?.given_name
    const cognitoLastName = idToken?.payload?.family_name
    const cognitoPuid = idToken?.payload?.['custom:puid']

    if (!cognitoToken) {
      try {
        const session = await Auth.currentSession()
        cognitoToken = session.getIdToken().getJwtToken()
      } catch (refreshError) {
        console.error('Failed to refresh token', refreshError)
        signInFormManager.setFailure(
          'Authentication failed. Please try signing in again.'
        )
        return
      }
    }

    const decoded = jwtDecode<CognitoJwtPayload>(cognitoToken)
    if (decoded?.['custom:pura_user_incomplete'] == 'true') {
      if (window.Pura?.accountPanel && cognitoEmail) {
        const socialProvider =
          sessionStorage.getItem('social_provider') || 'social'
        let firstName = ''
        let lastName = ''

        if (decoded.name) {
          const nameParts = decoded.name.trim().split(' ')
          firstName = nameParts[0] || ''
          lastName = nameParts.slice(1).join(' ') || ''
        } else {
          firstName = decoded.given_name || ''
          lastName = decoded.family_name || ''
        }

        window.Pura.accountPanel.showSocialIncompletePanel(
          cognitoEmail,
          return_to_url,
          socialProvider,
          firstName,
          lastName
        )
        return
      }
    }

    if (cognitoEmail) {
      const identifyTraits: any = {
        form_url: window.location.href,
        form_id: 'email-sign-in-form',
      }

      identifyTraits.email = cognitoEmail
      if (cognitoFirebaseID) identifyTraits.firebaseId = cognitoFirebaseID
      if (cognitoPuid) identifyTraits.puid = cognitoPuid
      if (cognitoFirstName) identifyTraits.firstName = cognitoFirstName
      if (cognitoLastName) identifyTraits.lastName = cognitoLastName

      segment.identify({
        ...identifyTraits,
      })
    }

    const result = await callMultipass(cognitoToken, return_to_url)

    signInFormManager.setVerified()

    window.location.href = result.signin_url
  } catch (error: any) {
    if (error.message.includes('Authentication failed: 401')) {
      const fallbackUrl = '/#show-account-panel'
      window.location.href = fallbackUrl
      return
    }

    signInFormManager.setFailure(`Authentication failed: ${error.message}`)
  }
}

// Helper function to display password error and reset form
const setPasswordError = (): void => {
  const inputGroup = $('#password-signin-password').closest('.input-group')
  const inlineError = inputGroup.find('.input-group-error')

  if (inlineError.length > 0) {
    inlineError.text('Incorrect password. Please try again.')
    inlineError.removeClass('hidden')
    inputGroup.addClass('invalid')
    inputGroup.attr('data-valid', 'false')
    inputGroup.attr('data-auth-error', 'true')
  }

  $('#password-signin-password').val('')
  passwordFormManager.reset()
}

const attemptMigrationAndRetry = async (
  email: string,
  password: string
): Promise<void> => {
  try {
    const migrationRes = await checkMigration(email, password)
    const migrationData = await migrationRes.json()

    if (migrationData.retry) {
      await Auth.signIn(email, password)
    }
  } catch (migrationError: any) {
    if (migrationError.status === 400) {
      setPasswordError()
    }
  }
}

const emailSignIn = async (email: string, password: string): Promise<void> => {
  try {
    await Auth.signIn(email, password)
  } catch (error: any) {
    if (error.code === 'UserNotConfirmedException') {
      if (window.Pura?.accountPanel) {
        window.Pura.accountPanel.showVerifyPanel(email, password)
      }
      return
    } else if (
      error.code === 'NotAuthorizedException' ||
      error.statusCode === 400
    ) {
      setPasswordError()
      return
    }

    await attemptMigrationAndRetry(email, password)
  }
}

const resendEmailCode = async (email: string): Promise<void> => {
  try {
    const response = await fetch(`${subsystemURL}/v1/users/check-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email }),
    })
    const responseData = await response.json()
    if (responseData?.nextAction == 'verifyEmailCodeSent') {
      const sessionId = responseData?.additionalData?.sessionId

      if (sessionId) {
        $('#email-verification-session-id').val(sessionId)
      }
      const messageElement = $('[data-content="email-verify-response-message"]')
      messageElement.text('A new code was sent to your email')
      messageElement.removeClass('hidden')

      // Reset the form manager to enable the button again after resending code
      emailVerifyFormManager.reset()

      setTimeout(() => {
        messageElement.addClass('hidden')
        messageElement.text('')
      }, 3000)
    }
  } catch (error) {
    console.error('resend email code error:', error)
  }
}

const checkEmail = async (email: string): Promise<void> => {
  try {
    const response = await fetch(`${subsystemURL}/v1/users/check-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email }),
    })

    if (!window.Pura?.accountPanel) {
      emailVerifyFormManager.setFailure('An error occurred. Please try again.')
      return
    }

    const responseData = await response.json()
    if (
      responseData?.nextAction == 'verifyEmailCodeSent' &&
      responseData?.additionalData?.sessionId
    ) {
      window.Pura.accountPanel.showEmailVerifyPanel(
        email,
        responseData?.additionalData?.sessionId
      )
    } else if (responseData.nextAction == 'cognitoPasswordAuth') {
      window.Pura.accountPanel.showPasswordPanel(email)
    } else {
      signInFormManager.setFailure('An error occurred. Please try again.')
    }
  } catch (error) {
    console.error('check email error:', error)
    signInFormManager.setFailure('An error occurred. Please try again.')
  }
}

const verifyEmailCode = async (
  email: string,
  sessionId: string,
  code: string
): Promise<void> => {
  try {
    const response = await fetch(`${subsystemURL}/v1/users/confirm-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        sessionId: sessionId,
        code: code,
      }),
    })
    const responseData = await response.json()
    if (!window.Pura?.accountPanel) {
      emailVerifyFormManager.setFailure('An error occurred. Please try again.')
      return
    }

    if (response.ok && responseData.nextAction == 'createUserFromEmail') {
      emailVerifyFormManager.setVerified()
      const puid = responseData.additionalData?.puid
      const firstName = responseData.additionalData?.firstName ?? ''
      const lastName = responseData.additionalData?.lastName ?? ''
      window.Pura.accountPanel.showCompleteSignupPanel(
        email,
        puid,
        code,
        sessionId,
        firstName,
        lastName
      )
    } else if (response.ok && responseData.nextAction == 'getCustomerData') {
      const customerDataResponse = await fetch(
        `${subsystemURL}/v1/customer-data?email=${encodeURIComponent(
          email
        )}&code=${encodeURIComponent(code)}&session_id=${encodeURIComponent(
          sessionId
        )}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      const customerDataResponseData = await customerDataResponse.json()
      if (
        customerDataResponseData.firstName ||
        customerDataResponseData.lastName
      ) {
        window.Pura.accountPanel.showCompleteSignupPanel(
          email,
          '',
          code,
          sessionId,
          customerDataResponseData.firstName ?? '',
          customerDataResponseData.lastName ?? ''
        )
      } else {
        window.Pura.accountPanel.showUnrecognizedEmailPanel(
          email,
          code,
          sessionId
        )
      }
    } else if (responseData.message == 'invalidCode') {
      const inputGroup = $('#email-verification-code').closest('.input-group')
      const errorField = inputGroup.find('.input-group-error')

      inputGroup.attr('data-valid', 'false')
      inputGroup.addClass('invalid')
      errorField.html('Invalid code. Please try again.')
      errorField.removeClass('hidden')

      emailVerifyFormManager.reset()
    } else if (responseData.message == 'invalidSession') {
      window.Pura.accountPanel.showSessionExpiredPanel(email)
      emailVerifyFormManager.reset()
    } else {
      emailVerifyFormManager.setFailure('An error occurred. Please try again.')
    }
  } catch (error) {
    console.error('Failed to verify email', error)
    emailVerifyFormManager.setFailure('An error occurred. Please try again.')
  }
}

$('#email-sign-in-form').on('submit', (e) => {
  e.preventDefault()
  signInFormManager.setLoading('Checking...')

  const form = $(e.target)
  const email = (form.find('#signin-email').val() as string).trim()
  checkEmail(email)
})

const completeSignup = async (
  email: string,
  puid: string,
  verificationCode: string,
  sessionId: string,
  firstName: string,
  lastName: string,
  password: string,
  formManager = completeSignupFormManager
): Promise<void> => {
  try {
    const response = await fetch(`${subsystemURL}/v1/users/create-from-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        firstName: firstName,
        lastName: lastName,
        password: password,
        authorizationData: {
          code: verificationCode,
          sessionId: sessionId,
        },
      }),
    })
    const responseData = await response.json()
    if (response.ok && responseData != null) {
      localStorage.setItem('puid', puid)
      formManager.setVerified()
      await emailSignIn(email, password)
      setCookie('login_redirect', location.href, 1)
    } else {
      formManager.setFailure('An error occurred. Please try again.')
    }
  } catch (error) {
    console.error('Failed to complete signup', error)
    formManager.setFailure('An error occurred. Please try again.')
  }
}

$(document).on('submit', '#account-email-verify-form', (e) => {
  e.preventDefault()
  emailVerifyFormManager.setLoading('Verifying...')

  const form = $(e.target)
  const email = (form.find('#email-verification-email').val() as string).trim()
  const sessionId = (
    form.find('#email-verification-session-id').val() as string
  ).trim()
  const code = (form.find('#email-verification-code').val() as string).trim()

  if (!email || !sessionId || !code) {
    emailVerifyFormManager.setFailure('An error occurred. Please try again.')
    return
  }

  verifyEmailCode(email, sessionId, code)
})

$(document).on('submit', '#account-password-form', (e) => {
  e.preventDefault()
  const form = $(e.target)
  const email = (form.find('#password-signin-email').val() as string).trim()
  const password = (
    form.find('#password-signin-password').val() as string
  ).trim()
  if (!email || !password) return
  passwordFormManager.setLoading('Signing in...')
  emailSignIn(email, password)
  setCookie('login_redirect', location.href, 1)
})

$(document).on('submit', '#account-complete-signup-form', (e) => {
  e.preventDefault()
  completeSignupFormManager.setLoading('Creating Account...')

  const form = $(e.target)
  const email = (form.find('#complete-signup-email').val() as string).trim()
  const puid = (form.find('#complete-signup-puid').val() as string).trim()
  const verificationCode = (
    form.find('#complete-signup-verification-code').val() as string
  ).trim()
  const sessionId = (
    form.find('#complete-signup-session-id').val() as string
  ).trim()
  const firstName = (
    form.find('#complete-signup-first-name').val() as string
  ).trim()
  const lastName = (
    form.find('#complete-signup-last-name').val() as string
  ).trim()
  const password = (
    form.find('#complete-signup-password').val() as string
  ).trim()
  const confirmPassword = (
    form.find('#complete-signup-confirm-password').val() as string
  ).trim()
  const agreeToTerms = form.find('#complete-signup-terms').is(':checked')

  if (
    !email ||
    !verificationCode ||
    !sessionId ||
    !firstName ||
    !lastName ||
    !password ||
    !confirmPassword
  ) {
    completeSignupFormManager.setFailure('Please fill in all required fields')
    return
  }

  if (password !== confirmPassword) {
    completeSignupFormManager.setFailure('Passwords do not match')
    return
  }

  if (!agreeToTerms) {
    completeSignupFormManager.setFailure(
      'Please agree to the Terms & Conditions'
    )
    return
  }

  completeSignup(
    email,
    puid,
    verificationCode,
    sessionId,
    firstName,
    lastName,
    password
  )
})

$(document).on('submit', '#account-complete-new-account-form', (e) => {
  e.preventDefault()
  completeNewAccountFormManager.setLoading('Creating Account...')

  const form = $(e.target)
  const email = (
    form.find('#complete-new-account-email').val() as string
  ).trim()
  const verificationCode = (
    form.find('#complete-new-account-verification-code').val() as string
  ).trim()
  const sessionId = (
    form.find('#complete-new-account-session-id').val() as string
  ).trim()
  const firstName = (
    form.find('#complete-new-account-first-name').val() as string
  ).trim()
  const lastName = (
    form.find('#complete-new-account-last-name').val() as string
  ).trim()
  const password = (
    form.find('#complete-new-account-password').val() as string
  ).trim()
  const confirmPassword = (
    form.find('#complete-new-account-confirm-password').val() as string
  ).trim()
  const agreeToTerms = form.find('#complete-new-account-terms').is(':checked')

  if (
    !email ||
    !verificationCode ||
    !sessionId ||
    !firstName ||
    !lastName ||
    !password ||
    !confirmPassword
  ) {
    completeNewAccountFormManager.setFailure(
      'Please fill in all required fields'
    )
    return
  }

  if (password !== confirmPassword) {
    completeNewAccountFormManager.setFailure('Passwords do not match')
    return
  }

  if (!agreeToTerms) {
    completeNewAccountFormManager.setFailure(
      'Please agree to the Terms & Conditions'
    )
    return
  }

  // Call completeSignup with empty puid since this is a new account
  completeSignup(
    email,
    '', // empty puid for new accounts
    verificationCode,
    sessionId,
    firstName,
    lastName,
    password,
    completeNewAccountFormManager
  )
})

const resetSignInForm = () => {
  signInFormManager.reset()
}

export {
  emailSignIn,
  hubSignIn,
  hubSignInFailure,
  checkEmail,
  resetSignInForm,
  resendEmailCode,
  callMultipass,
}
